import Vue from 'vue';
import Vuex from 'vuex';
import authStore from './modules/authentication';
import dataStore from './modules/data';
import helpStore from './modules/whatToDo';
import rssStore from './modules/rss';
import alertsSuspiciousTrafficStore from './modules/alertsSuspiciousTraffic';
import alertsRoutingStatusStore from './modules/alertsRoutingStatus';
import routingStatusStore from './modules/routingStatus';
import { SELECTED_COMBO_BOX_TYPE } from '../constants/common';

Vue.use(Vuex);

let debounceTimerOfSetScreenWidth = null;

export default new Vuex.Store({
  state: {
    generatePDF: false,
    initRoute: null,
    showSideBar: false,
    showReportModal: false,
    isSmallerScreen: false,
    showReportWarning: false,
    isMobile: false,
    isExpandSidebar: true,
    isShowSidebarOnMobile: false,
    isLargeScreen: true,
    isExtraLargeScreen: true,
    isShowSessionExpiredModal: false,
    tokenExpiredHandlerTimer: null,
    isShowErrorInfoBanner: false,
    errorInfoBannerText: '',
    selectedComboBox: SELECTED_COMBO_BOX_TYPE.NONE,
    isExtraExtraLargeScreen: true,
    isSmallScreen: true,
    isSuccessfulSummaryRequest: false,
    isCheckingAuthentication: false,
    screenWidth: document?.documentElement?.clientWidth ?? 0,
    isAPNICStaff: false,
    isNavigatingToRSPageAfterLogin: false
  },
  getters: {
    generatePDF: state => state.generatePDF,
    initRoute: state => state.initRoute,
    showSideBar: state => state.showSideBar,
    isSmallerScreen: state => state.isSmallerScreen,
    showReportWarning: state => state.showReportWarning,
    showReportModal: state => state.showReportModal,
    isMobile: state => state.isMobile,
    isLargeScreen: state => state.isLargeScreen,
    isExtraLargeScreen: state => state.isExtraLargeScreen,
    isContextWrapped: state =>
      !state.isMobile && !state.isLargeScreen && state.isExpandSidebar,
    screenWidth: state => state.screenWidth
  },
  mutations: {
    setGeneratePDF: (state, boolean) => {
      state.generatePDF = boolean;
    },
    setInitRoute: (state, route) => {
      state.initRoute = route;
    },
    setShowSideBar: (state, bool) => {
      state.showSideBar = bool;
    },
    setIsSmallerScreen: (state, bool) => {
      state.isSmallerScreen = bool;
    },
    setIsSmallerScreen: (state, bool) => {
      state.isSmallerScreen = bool;
    },
    setShowReportWarning: (state, bool) => {
      state.showReportWarning = bool;
    },
    setShowReportModal: (state, bool) => {
      state.showReportModal = bool;
    },
    setIsMobile: (state, bool) => {
      state.isMobile = bool;
    },
    setIsExpandSidebar: (state, bool) => {
      state.isExpandSidebar = bool;
    },
    setIsShowSidebarOnMobile: (state, bool) => {
      state.isShowSidebarOnMobile = bool;
    },
    setIsLargeScreen: (state, bool) => {
      state.isLargeScreen = bool;
    },
    setIsExtraLargeScreen: (state, bool) => {
      state.isExtraLargeScreen = bool;
    },
    setIsShowSessionExpiredModal: (state, bool) => {
      state.isShowSessionExpiredModal = bool;
    },
    setTokenExpiredHandlerTimer: (state, val) => {
      state.tokenExpiredHandlerTimer = val;
    },
    setIsShowErrorInfoBanner: (state, bool) => {
      state.isShowErrorInfoBanner = bool;
    },
    setErrorInfoBannerText: (state, str) => {
      state.errorInfoBannerText = str;
    },
    setSelectedComboBox: (state, type) => {
      state.selectedComboBox = type;
    },
    setIsExtraExtraLargeScreen: (state, bool) => {
      state.isExtraExtraLargeScreen = bool;
    },
    setIsSmallScreen: (state, bool) => {
      state.isSmallScreen = bool;
    },
    setIsSuccessfulSummaryRequest: (state, bool) => {
      state.isSuccessfulSummaryRequest = bool;
    },
    setIsCheckingAuthentication: (state, bool) => {
      state.isCheckingAuthentication = bool;
    },
    setScreenWidth: (state, val) => {
      state.screenWidth = val;
    },
    setIsAPNICStaff: (state, bool) => {
      state.isAPNICStaff = bool;
    },
    setIsNavigatingToRSPageAfterLogin: (state, bool) => {
      state.isNavigatingToRSPageAfterLogin = bool;
    }
  },
  actions: {
    startDownloadPDF: context => {
      context.commit('setGeneratePDF', true);
    },
    endDownloadPDF: context => {
      context.commit('setGeneratePDF', false);
    },
    toggleSideBar: context => {
      context.commit('setShowSideBar', !context.state.showSideBar);
    },
    updateScreenWidth: ({ commit }, width) => {
      // Debounce setScreenWidth
      if (debounceTimerOfSetScreenWidth)
        clearTimeout(debounceTimerOfSetScreenWidth);

      debounceTimerOfSetScreenWidth = setTimeout(() => {
        commit('setScreenWidth', width);
      }, 300);

      commit('setIsSmallerScreen', width <= 768);

      const isMobile = checkIsMobileDevice(width);
      commit('setIsMobile', isMobile);

      const isSmallScreen = width >= 576;
      commit('setIsSmallScreen', isSmallScreen);

      const isLargeScreen = width >= 992;
      commit('setIsLargeScreen', isLargeScreen);

      const isExtraLargeScreen = width >= 1200;
      commit('setIsExtraLargeScreen', isExtraLargeScreen);

      const isExtraExtraLargeScreen = width >= 1400;
      commit('setIsExtraExtraLargeScreen', isExtraExtraLargeScreen);
    },
    showReportWarning: ({ commit }) => {
      commit('setShowReportWarning', true);
    },
    setErrorInfoBanner: (context, err) => {
      if (err.response && err.response.data && err.response.data.message) {
        context.commit('setErrorInfoBannerText', err.response.data.message);
        context.commit('setIsShowErrorInfoBanner', true);
      } else if (err.message) {
        context.commit('setErrorInfoBannerText', err.message);
        context.commit('setIsShowErrorInfoBanner', true);
      } else if (err) {
        context.commit('setErrorInfoBannerText', err);
        context.commit('setIsShowErrorInfoBanner', true);
      } else {
        context.commit(
          'setErrorInfoBannerText',
          'There was a problem processing your request and we are investigating. Please try again later.'
        );
        context.commit('setIsShowErrorInfoBanner', true);
      }
    }
  },
  modules: {
    authStore,
    dataStore,
    rssStore,
    helpStore,
    alertsSuspiciousTrafficStore,
    routingStatusStore,
    alertsRoutingStatusStore
  }
});

function checkIsMobileDevice(width) {
  const regexpMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isNarrowDevice = width < 768;

  if (regexpMobileDevice.test(navigator.userAgent) || isNarrowDevice)
    return true;

  return false;
}
